import { ArrowLeft, ArrowRight, ExternalLink, Filter, Home } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';
import { RealEstate, useRealEstateSystem } from '../../hooks/useRealEstateSystem';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { RealEstateDetails } from '../realestate/RealEstateDetails';

interface FilterState {
  category: string | null;
  subcategory: string | null;
  tags: Set<string>;
  search: string;
}

export function RealEstateApp() {
  const { chatbotPublicId } = useChatbot();
  const { properties, categories, tags, settings, loading, error, initialized, initialize } = useRealEstateSystem();
  const { colors } = useDesignColors();
  const { theme } = useThemeSettings();
  const { settings: multilingualSettings } = useMultilingualSettings();
  const [filters, setFilters] = useState<FilterState>({
    category: null,
    subcategory: null,
    tags: new Set(),
    search: ''
  });
  const [selectedProperty, setSelectedProperty] = useState<RealEstate | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isDark = theme.mode === 'dark';

  const hasActiveFilters = filters.category || filters.subcategory || filters.tags.size > 0 || filters.search;

  // Get translations for the current language
  const currentLang = multilingualSettings.currentLanguage || 'fr';
  const translations = multilingualSettings.translations[currentLang];
  const realEstateTranslations = translations?.miniApps?.find(app => app.id === 'realestate')?.content;

  // Function to get translated property data
  const getTranslatedProperty = (property: RealEstate) => {
    const translation = realEstateTranslations?.propertyDetails?.[property.id];
    return {
      ...property,
      title: translation?.title || property.title,
      description: translation?.description || property.description,
      features: translation?.features || property.features,
      category: translation?.category || property.category,
      subcategory: translation?.subcategory || property.subcategory,
      type: translation?.type || property.type,
      location: translation?.location || property.location
    };
  };

  // Function to get number of active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.category) count++;
    if (filters.subcategory) count++;
    count += filters.tags.size;
    if (filters.search) count++;
    return count;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setShowSidebar(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initialize(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initialize]);

  const filteredProperties = properties
    .map(getTranslatedProperty)
    .filter(property => {
      if (filters.search && !property.title.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.category && property.category !== filters.category) {
        return false;
      }
      if (filters.subcategory && property.subcategory !== filters.subcategory) {
        return false;
      }
      if (filters.tags.size > 0) {
        // Vérifier si la propriété a au moins un des tags sélectionnés
        return property.tags?.some(tagId => {
          const tag = tags.find(t => t.id === tagId);
          return tag && filters.tags.has(tag.name);
        });
      }
      return true;
    });

  const resetFilters = () => {
    setFilters({
      category: null,
      subcategory: null,
      tags: new Set(),
      search: ''
    });
  };

  const handleCategoryClick = (categoryName: string) => {
    setFilters(prev => ({
      ...prev,
      category: prev.category === categoryName ? null : categoryName,
      subcategory: null
    }));
  };

  const handleSubcategoryClick = (subcategoryName: string) => {
    setFilters(prev => ({
      ...prev,
      subcategory: prev.subcategory === subcategoryName ? null : subcategoryName
    }));
  };

  const handleTagClick = (tagName: string) => {
    setFilters(prev => {
      const newTags = new Set(prev.tags);
      if (newTags.has(tagName)) {
        newTags.delete(tagName);
      } else {
        newTags.add(tagName);
      }
      return { ...prev, tags: newTags };
    });
  };

  if (loading.initialize && !initialized) {
    return (
      <div className="flex justify-center items-center h-[600px]">
        <div 
          className="animate-spin rounded-full h-8 w-8 border-2"
          style={{ borderColor: `${colors.gradientStart} transparent ${colors.gradientEnd} transparent` }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-400">
        {error}
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center p-8">
        <div 
          className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)` }}
        >
          <Home 
            className="w-8 h-8"
            style={{ color: colors.gradientStart }}
          />
        </div>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {realEstateTranslations?.noPropertiesMessage || 'No real estate properties available'}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-[600px] flex relative">
      {/* Sidebar avec filtres */}
      <div 
        className={`
          ${isMobile ? 'fixed inset-y-0 left-0 z-50 w-full max-w-sm' : 'w-64'} 
          transform transition-transform duration-300 ease-in-out
          ${isMobile && !showSidebar ? '-translate-x-full' : 'translate-x-0'}
          ${isDark ? 'bg-gray-900/95' : 'bg-white/95'} 
          backdrop-blur-xl
          border-r ${isDark ? 'border-white/10' : 'border-gray-200'}
          overflow-hidden
          flex flex-col
        `}
      >
        {isMobile && (
          <div className="flex justify-between items-center p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSidebar(false)}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400" />
              </button>
              <h3 className="text-white font-medium">
                {realEstateTranslations?.filterLabel || 'Filtres'}
              </h3>
            </div>
            {hasActiveFilters && (
              <button
                onClick={() => setShowSidebar(false)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm"
                style={{
                  background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                }}
              >
                <span className="text-white">
                  {realEstateTranslations?.viewPropertiesLabel || `See ${filteredProperties.length} property`}
                </span>
                <ArrowRight className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        )}

        <div className="p-4 h-full overflow-y-auto">
          <div className="space-y-4">
            {/* Recherche */}
            <div className="relative">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder={realEstateTranslations?.searchPlaceholder || "Search for a property..."}
                className={`w-full pl-3 pr-4 py-2 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-black/20 border-white/5 text-white placeholder-gray-500' 
                    : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-400'
                } border focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              />
            </div>

            {/* Filtres actifs */}
            {(filters.category || filters.subcategory || filters.tags.size > 0 || filters.search) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                {filters.category && (
                  <div 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <span>{filters.category}</span>
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, category: null, subcategory: null }))}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {filters.subcategory && (
                  <div 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <span>{filters.subcategory}</span>
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, subcategory: null }))}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {Array.from(filters.tags).map(tag => (
                  <div 
                    key={tag} 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <span>{tag}</span>
                    <button
                      onClick={() => handleTagClick(tag)}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {filters.search && (
                  <div 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <span>"{filters.search}"</span>
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <button
                  onClick={resetFilters}
                  className="px-2 py-1 rounded-lg text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  {realEstateTranslations?.resetFiltersLabel || 'Reset'}
                </button>
              </div>
            )}

            {/* Catégories */}
            {categories.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {realEstateTranslations?.categoriesLabel || 'Categories'}
                </h4>
                <div className="space-y-1">
                  {categories
                    .filter(category => !category.parent_id)
                    .map(category => (
                      <div key={category.id} className="space-y-1">
                        <button
                          onClick={() => handleCategoryClick(category.name)}
                          className={`w-full px-3 py-2 rounded-lg flex items-center justify-between transition-all ${
                            filters.category === category.name
                              ? 'bg-gradient-to-r'
                              : isDark
                                ? 'hover:bg-white/5 text-gray-300'
                                : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          style={filters.category === category.name ? {
                            backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                            color: colors.gradientStart
                          } : undefined}
                        >
                          <span>{category.name}</span>
                        </button>

                        {filters.category === category.name && (
                          <div className="ml-4 space-y-1">
                            {categories
                              .filter(cat => cat.parent_id === category.id)
                              .map(subcategory => (
                                <button
                                  key={subcategory.id}
                                  onClick={() => handleSubcategoryClick(subcategory.name)}
                                  className={`w-full px-3 py-2 rounded-lg flex items-center justify-between transition-all ${
                                    filters.subcategory === subcategory.name
                                      ? 'bg-gradient-to-r'
                                      : isDark
                                        ? 'hover:bg-white/5 text-gray-300'
                                        : 'hover:bg-gray-100 text-gray-700'
                                  }`}
                                  style={filters.subcategory === subcategory.name ? {
                                    backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                                    color: colors.gradientStart
                                  } : undefined}
                                >
                                  <span>{subcategory.name}</span>
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {realEstateTranslations?.tagsLabel || 'Tags'}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => handleTagClick(tag.name)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm transition-all ${
                        filters.tags.has(tag.name)
                          ? 'bg-gradient-to-r'
                          : isDark
                            ? 'bg-white/5 text-gray-300 hover:bg-white/10'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={filters.tags.has(tag.name) ? {
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                        color: colors.gradientStart
                      } : undefined}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay pour mobile */}
      {isMobile && showSidebar && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Liste des biens */}
      <div className={`flex-1 h-full overflow-y-auto ${
        isDark ? 'bg-transparent' : 'bg-white'
      }`}>
        <div className="p-4">
          <div className={`grid ${isMobile ? 'grid-cols-1' : `grid-cols-${settings.gridColumns}`} gap-4`}>
            {filteredProperties.map((property) => (
              <div
                key={property.id}
                className={`group relative overflow-hidden rounded-xl border transition-all ${
                  isDark 
                    ? 'bg-gray-800/50 border-white/5 hover:border-indigo-500/50' 
                    : 'bg-white border-black/5 hover:border-indigo-500/50'
                }`}
              >
                <button
                  onClick={() => setSelectedProperty(property)}
                  className="w-full text-left"
                >
                  {property.images?.[0] && (
                    <div className="aspect-[4/3] w-full">
                      <img
                        src={property.images[0]}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="p-6 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <h4 
                        className="text-xl font-medium bg-clip-text text-transparent bg-gradient-to-r"
                        style={{
                          backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                        }}
                      >
                        {property.title}
                      </h4>
                      {property.metadata?.external_url && (
                        <a
                          href={property.metadata.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`shrink-0 p-1 ${
                            isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                          } transition-colors`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>

                    {property.description && (
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        {property.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 text-base">
                      {settings.showPrices && property.price && (
                        <div 
                          className="font-medium text-lg bg-clip-text text-transparent bg-gradient-to-r"
                          style={{
                            backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                          }}
                        >
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: property.currency,
                            maximumFractionDigits: 0
                          }).format(property.price)}
                          {property.transaction_type === 'rental' && '/month'}
                        </div>
                      )}
                      {settings.showSurface && property.surface && (
                        <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                          {property.surface} m²
                        </div>
                      )}
                      {settings.showRooms && property.rooms && (
                        <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                          {property.rooms} rooms
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {property.type && (
                        <span className={`px-3 py-1.5 rounded-lg text-sm ${
                          isDark 
                            ? 'bg-white/5 text-gray-300' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {property.type}
                        </span>
                      )}
                      {property.transaction_type && (
                        <span className={`px-3 py-1.5 rounded-lg text-sm ${
                          isDark 
                            ? 'bg-white/5 text-gray-300' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {property.transaction_type === 'sale' ? 'Vente' : 'Location'}
                        </span>
                      )}
                      {property.status && (
                        <span className={`px-3 py-1.5 rounded-lg text-sm ${
                          property.status === 'available' 
                            ? 'bg-green-500/20 text-green-400'
                            : property.status === 'sold'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {property.status === 'available' ? 'Available' : property.status === 'sold' ? 'Sold' : 'Sold'}
                        </span>
                      )}
                      {/* Affichage des tags */}
                      {property.tags?.map(tagId => {
                        const tag = tags.find(t => t.id === tagId);
                        if (tag) {
                          return (
                            <span key={tag.id} className={`px-3 py-1.5 rounded-lg text-sm ${
                              isDark 
                                ? 'bg-white/5 text-gray-300' 
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {tag.name}
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bouton toggle filtres mobile avec badge */}
      {isMobile && !showSidebar && (
        <button
          onClick={() => setShowSidebar(true)}
          className="fixed bottom-4 left-4 p-3 rounded-full shadow-lg z-40 flex items-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`
          }}
        >
          <Filter className="w-5 h-5 text-white" />
          {getActiveFiltersCount() > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              {getActiveFiltersCount()}
            </span>
          )}
        </button>
      )}

      {/* Modal détails bien */}
      {selectedProperty && (
        <RealEstateDetails
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
        />
      )}
    </div>
  );
}